
interface PendingRequest<T> {
  promise: Promise<T>;
  timestamp: number;
}

class RequestDeduplicator {
  private pendingRequests = new Map<string, PendingRequest<any>>();
  private requestTimeout = 30000; // 30 seconds timeout

  async deduplicate<T>(
    key: string,
    requestFn: () => Promise<T>,
    options = { timeout: this.requestTimeout }
  ): Promise<T> {
    // Clean up expired requests
    this.cleanupExpiredRequests();
    
    // Check if there's already a pending request with this key
    const existing = this.pendingRequests.get(key);
    if (existing) {
      // Silently reuse existing request - no console spam
      return existing.promise;
    }
    
    // Create new request
    const promise = requestFn().finally(() => {
      // Clean up after completion
      this.pendingRequests.delete(key);
    });
    
    // Store the pending request
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now()
    });
    
    return promise;
  }
  
  private cleanupExpiredRequests() {
    const now = Date.now();
    for (const [key, request] of this.pendingRequests.entries()) {
      if (now - request.timestamp > this.requestTimeout) {
        // Silent cleanup - no console spam
        this.pendingRequests.delete(key);
      }
    }
  }
  
  getStats() {
    return {
      pendingCount: this.pendingRequests.size,
      pendingKeys: Array.from(this.pendingRequests.keys())
    };
  }
  
  clear() {
    this.pendingRequests.clear();
  }
}

// Global instance
export const requestDeduplicator = new RequestDeduplicator();

// Helper function for common use cases
export function deduplicateRequest<T>(
  key: string,
  requestFn: () => Promise<T>
): Promise<T> {
  return requestDeduplicator.deduplicate(key, requestFn);
}
